export interface MaskData {
  data: Uint8Array;
  width: number;
  height: number;
}

export interface ImageAnnotationData {
  annotation_uuid: string;
  image_uuid: string;
  label: string;
  size: number[];
  image_url: string;
  segmentation_url: string;
  cutout_url: string;
}

export interface SamMask {
  mask: {
    data: Uint8Array;
    width: number;
    height: number;
  };
  scores: number[];
}

export enum VisionProjectType {
  CADPicking = 'cad_picking',
  LabelPicking = 'label_picking',
  Depalletizing = 'depalletizing',
}
export interface VisionProject {
  uuid: string;
  name: string;
  creator_uuid: string;
  project_type: VisionProjectType;
  is_creator: boolean;
  archived: boolean;
  // null/undefined = never chosen; the labeling editor asks on first open.
  rotation_labeling?: boolean | null;
}

export interface ProjectMember {
  userUuid: string;
  email: string;
  name: string;
  isCreator: boolean;
}

export interface CADModelData {
  cad_model_uuid?: string;
  label?: string;
  texture: string;
  rgba_value: string;
  metadata: string;
  file: string;
}

export interface NewCADProject {
  file: File | undefined;
  images: File[];
  texture: string;
  rgba_value: string;
}

export const EMPTY_NEW_CAD_PROJECT: NewCADProject = {
  file: undefined,
  images: [],
  texture: 'Metal',
  rgba_value: '#',
};

/** partSymmetry value meaning the part's rotation is irrelevant (no rotation is trained). */
export const PART_SYMMETRY_ROTATION_NOT_REQUIRED = 0;

export interface AdvancedSettings {
  ropeFlip: boolean;
  keepTextures: boolean;
  /** Rotational symmetry of the part in degrees (must divide 360), or 0 = rotation not required. */
  partSymmetry: number;
  /** Rotation classifier step of the deprecated Mask R-CNN model; only set after such a training. */
  resolutionAngle?: number | null;
}

/** Suggested rotation classifier step when training the deprecated model. */
export const DEFAULT_DEPRECATED_RESOLUTION_ANGLE = 10;

export const DEFAULT_CAD_ADVANCED_SETTINGS: AdvancedSettings = {
  ropeFlip: false,
  keepTextures: false,
  partSymmetry: 360,
};

export const DEFAULT_IMAGE_ADVANCED_SETTINGS: AdvancedSettings = {
  ropeFlip: false,
  keepTextures: true,
  partSymmetry: 360,
};

export enum TrainingStatus {
  STARTING = 'STARTING', // The job has been submitted to Anyscale and is initializing
  RUNNING = 'RUNNING', // During the Ray container execution
  SUCCEEDED = 'SUCCEEDED', // When the Anyscale job was completed successfully
  FAILED = 'FAILED', // When the Anyscale job was completed unsuccessfully
  CANCELING = 'CANCELING', // Cancel accepted, job termination not yet confirmed
  CANCELED = 'CANCELED', // Stopped by the user before finishing
  UNKNOWN = 'UNKNOWN', // Unexpected state returned from Anyscale
}

// Statuses that may still occupy the project's single training slot (and be
// billing) — mirrors the backend's ACTIVE_TRAINING_STATUSES: exactly what
// the cancel endpoint accepts and what the UI treats as "in progress".
export const ACTIVE_TRAINING_STATUSES: readonly TrainingStatus[] = [
  TrainingStatus.STARTING,
  TrainingStatus.RUNNING,
  TrainingStatus.UNKNOWN,
  TrainingStatus.CANCELING,
];

export interface TrainingData {
  status: TrainingStatus;
  runId: string;
}

/** One row of the model-versions table (GET …/trainings, newest first). */
export interface ProjectTrainingRun {
  runId: string;
  // Custom display name given at trigger time (null = unnamed).
  name?: string | null;
  status: TrainingStatus;
  createdAt: string;
  // Sequential version stamped by the backend (null on failed/canceled runs
  // and rows recorded before versioning — shown as "—", never invented).
  modelVersion?: number | null;
  // null on runs recorded before the flag existed — unknown lineage.
  baseModel: boolean | null;
  // Labeling-dataset version the run was trained on (null for base models,
  // manual uploads and runs recorded before the stamp existed).
  datasetVersion?: number | null;
  deployments: Deployment[];
}

export interface NewDeployment {
  projectUuid: string;
  serialNumber: string;
  // Deploy this specific (successful) run — the rollback path. Omitted =
  // the newest successful run.
  runId?: string;
}

export const EMPTY_NEW_DEPLOYMENT: NewDeployment = {
  projectUuid: '',
  serialNumber: '',
};

export enum DeploymentStatus {
  Starting = 'starting',
  Deploying = 'deploying',
  TimeoutEdgeDevice = 'timeout_edge_device',
  TimeoutRobot = 'timeout_robot',
  TimeoutFileTransfer = 'timeout_file_transfer',
  Failed = 'failed',
  Success = 'success',
}

export interface Deployment {
  serialNumber: string;
  status: DeploymentStatus;
}

export enum LabelingImageStatus {
  Unlabeled = 'unlabeled',
  Prelabeled = 'prelabeled',
  InProgress = 'in_progress',
  Ready = 'ready',
}

export interface LabelingImage {
  uuid: string;
  status: LabelingImageStatus;
  sourceName: string;
  embeddingReady: boolean;
  instanceCount: number | null;
  classes: string[] | null;
  lastModified: string | null;
  createdAt: string;
  // Presigned color.jpg URL for thumbnails/grids (contract addition).
  imageUrl?: string;
}

export interface LabelingImportResult {
  imported: number;
  skippedFolders: string[];
  duplicateFolders: string[];
  images: LabelingImage[];
}

export interface LabelingLock {
  lockedBy: string;
  lockedByName: string;
  expiresAt: number;
  heldByMe: boolean;
}

export enum LabelingDatasetStatus {
  Building = 'building',
  Ready = 'ready',
  Failed = 'failed',
}

export interface LabelingDataset {
  version: number;
  status: LabelingDatasetStatus;
  imageCount: number;
  createdBy: string;
  createdAt: string;
  error: string | null;
}

export interface LabelingImageFiles {
  imageUrl: string;
  infoUrl: string;
  // null when no annotations were ever saved for this image. Writes go
  // through PUT …/images/{uuid}/annotations, not a presigned URL.
  annotationsUrl: string | null;
}

export interface LabelingEmbeddingInfo {
  url: string;
  metaUrl?: string;
}

export interface SamAssets {
  positionalEmbeddingUrl: string;
  // Presigned meta JSON next to the tensor (model_name identifies the
  // encoder checkpoint and versions the browser-side asset cache).
  positionalEmbeddingMetaUrl: string;
}

export interface SamEmbeddingMeta {
  model_name: string;
  original_size: [number, number];
  reshaped_input_size: [number, number];
}

// Per-image COCO document — the exact format the backend dataset build
// consumes (merge_annotation_files): one image entry, polygon segmentation,
// keypoints [x, y, visibility], per-instance rotation (stripped at export).
export interface CocoAnnotation {
  id: number;
  image_id: number;
  category_id: number;
  segmentation: number[][];
  bbox: [number, number, number, number];
  area: number;
  keypoints?: number[];
  rotation?: number;
}

export interface PerImageCocoDoc {
  images: Array<{
    id: number;
    file_name: string;
    width: number;
    height: number;
  }>;
  annotations: CocoAnnotation[];
  categories: Array<{ id: number; name: string }>;
}
